@file:Suppress("MagicNumber", "TooGenericExceptionCaught", "TooManyFunctions")

package com.nonopn.skydome.common.app.pages.dashboard

import com.nonopn.dispatchers.DefaultDispatcher
import com.nonopn.skydome.common.app.api.impl.UpdateProbe
import com.nonopn.skydome.common.app.api.model.DashboardImpactReportV2
import com.nonopn.skydome.common.app.detection.ThunderDetection
import com.nonopn.skydome.common.app.home.AppModel
import com.nonopn.skydome.common.app.pages.main.model.DocumentHistoryItem
import com.nonopn.skydome.common.app.pages.main.model.api.Project
import com.nonopn.skydome.common.app.pages.probes.AccountActions
import com.nonopn.skydome.common.app.pages.probes.ProbeStatusInput
import com.nonopn.skydome.common.app.pages.probes.accountActions
import com.nonopn.skydome.common.app.pages.probes.isProbeDeleted
import com.nonopn.skydome.common.app.pages.probes.loadAccountStatus
import com.nonopn.skydome.common.app.pages.probes.nowSecondsUtc
import com.nonopn.skydome.common.app.pages.probes.visibleProbes
import com.nonopn.skywatcher.app.utils.safeLaunch
import eu.codlab.viewmodel.StateViewModel
import korlibs.time.DateTimeTz
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.async

// ─── Data classes ─────────────────────────────────────────────────────────────

data class ProbeItem(
    val uuid: String,
    val name: String,
    val lat: Double = 0.0,
    val lon: Double = 0.0
)

data class DailyBar(val label: String, val count: Int, val negCount: Int = 0, val posCount: Int = 0)

data class MonthlyBar(
    val label: String,
    val year: Int,
    val month: Int,
    val count: Int,
    val negCount: Int = 0,
    val posCount: Int = 0
)

data class IntensityBand(
    val label: String,
    val negCount: Int,
    val posCount: Int
) {
    val total: Int get() = negCount + posCount
}

data class TopStormDay(
    val dateLabel: String,
    val count: Int,
    val negCount: Int = 0,
    val posCount: Int = 0
)

data class PeriodStats(
    val total: Int = 0,
    val negCount: Int = 0,
    val posCount: Int = 0,
    val intensityBands: List<IntensityBand> = emptyList(),
    val maxNegKa: Int = 0,
    val maxNegDate: String = "–",
    val minNegKa: Int = 0,
    val minNegDate: String = "–",
    val avgNegKa: Int = 0,
    val maxPosKa: Int = 0,
    val maxPosDate: String = "–",
    val minPosKa: Int = 0,
    val minPosDate: String = "–",
    val avgPosKa: Int = 0
)

data class DashboardState(
    val loading: Boolean = false,
    val error: Boolean = false,
    val probeList: List<ProbeItem> = emptyList(),
    val projects: List<Project> = emptyList(),
    val lastUpdate: String? = null,
    val selectedProbeUuid: String? = null,
    val selectedReport: DashboardImpactReportV2? = null,
    val loadedReports: List<DashboardImpactReportV2>? = null,
    // Documents history (local cache, then server), used by the map pop-up of a site
    val documentHistory: List<DocumentHistoryItem> = emptyList(),
    val isHistoryReady: Boolean = false,
    // Billing status of the account probes (promo card and trial reminder)
    val probeInputs: List<ProbeStatusInput> = emptyList(),
    val probesLoaded: Boolean = false,
    // What the account can do according to GET /api/account/status (null = route failed: previous behaviour)
    val accountActions: AccountActions? = null,
)

// ─── ViewModel ────────────────────────────────────────────────────────────────

@Suppress("LargeClass")
class DashboardModel(
    private val appModel: AppModel
) : StateViewModel<DashboardState>(DashboardState()) {

    private val backgroundScope = CoroutineScope(DefaultDispatcher + SupervisorJob())

    // All reports from API v2 — one entry per probe (no null/total entry currently)
    private var reportsV2: List<DashboardImpactReportV2> = emptyList()

    init {
        load()
    }

    fun selectProbe(uuid: String?) {
        updateState {
            copy(
                selectedProbeUuid = uuid,
                selectedReport = loadedReports?.firstOrNull { it.probe?.uuid == uuid }
            )
        }
        recompute()
    }

    fun confirmRename(probeUuid: String, newName: String) = safeLaunch {
        val trimmed = newName.trim()
        if (trimmed.isBlank()) return@safeLaunch
        try {
            val api = appModel.states.value.currentApi ?: return@safeLaunch
            val probe = states.value.probeList.find { it.uuid == probeUuid } ?: return@safeLaunch
            val thunder = ThunderDetection(
                uuid = probe.uuid,
                name = probe.name,
                latitude = probe.lat,
                longitude = probe.lon
            )
            api.updateProbe(thunder, UpdateProbe(name = trimmed))
            reportsV2 = reportsV2.map { report ->
                if (report.probe?.uuid == probeUuid) {
                    report.copy(probe = report.probe.copy(name = trimmed))
                } else {
                    report
                }
            }
            updateState {
                copy(
                    loadedReports = reportsV2,
                    probeList = probeList
                        .map { if (it.uuid == probeUuid) it.copy(name = trimmed) else it }
                        .sortedBy { it.name }
                )
            }
            recompute()
        } catch (e: Throwable) {
            e.printStackTrace()
            updateState { copy(error = true) }
        }
    }

    fun load() = safeLaunch {
        backgroundScope.async {
            updateState { copy(loading = true, error = false) }
            try {
                reportsV2 = appModel.cachedDashboardReportsV2()
                val probes = reportsV2
                    .mapNotNull { it.probe }
                    .mapIndexed { idx, it ->
                        val label = it.name.ifBlank { "Sonde #${idx + 1}" }
                        ProbeItem(it.uuid, label, it.latitude, it.longitude)
                    }
                    .distinctBy { it.uuid }
                    .sortedBy { it.name }
                val projectsList = try {
                    appModel.states.value.currentApi?.estimatesData()
                        ?.map { it.project } ?: emptyList()
                } catch (_: Throwable) {
                    emptyList()
                }
                // Billing status of the account: the status route first, the reports as a fallback
                val status = appModel.loadAccountStatus()
                val inputs = status?.visibleProbes(nowSecondsUtc())?.map { it.second }
                    ?: reportsV2.mapNotNull { it.probe }
                        .filterNot { isProbeDeleted(it.status) }.map { it.statusInput() }
                updateState {
                    copy(
                        probeList = probes, projects = projectsList,
                        loadedReports = reportsV2,
                        selectedReport = reportsV2.firstOrNull { it.probe?.uuid == selectedProbeUuid },
                        probeInputs = inputs,
                        probesLoaded = true,
                        accountActions = status?.accountActions(),
                    )
                }
                recompute()
                loadDocumentHistory()
            } catch (e: Throwable) {
                e.printStackTrace()
                updateState { copy(loading = false, error = true) }
            }
        }
    }

    // Best effort: the local cache first, then the server history. Failures never block the dashboard.
    private suspend fun loadDocumentHistory() {
        try {
            val local = appModel.getSpecificData()?.documentHistory.orEmpty()
            updateState { copy(documentHistory = local, isHistoryReady = true) }
            appModel.syncDocumentHistory(local)?.let { synced ->
                updateState { copy(documentHistory = synced) }
            }
        } catch (e: Throwable) {
            e.printStackTrace()
        }
    }

    @Suppress("LongMethod")
    private fun recompute() {
        val selectedUuid = states.value.selectedProbeUuid

        val effectiveReport =
            states.value.loadedReports?.firstOrNull { it.probe?.uuid == selectedUuid }

        if (effectiveReport == null) {
            updateState { copy(loading = false) }
            return
        }

        val now = DateTimeTz.nowLocal()
        val lastUpdate = "${now.hours.toString().padStart(2, '0')}:" +
                "${now.minutes.toString().padStart(2, '0')} UTC"

        updateState {
            copy(
                loading = false,
                lastUpdate = lastUpdate,
            )
        }
    }
}

// ─── Extensions ───────────────────────────────────────────────────────────────

// Formats an integer with thousands separators: 305466 → "305 466"
internal fun Int.formatThousands(): String {
    if (this < 1000) return toString()
    val digits = toString()
    val sb = StringBuilder()
    digits.reversed().forEachIndexed { i, c ->
        if (i > 0 && i % 3 == 0) sb.append(' ')
        sb.append(c)
    }
    return sb.reverse().toString()
}
